import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Toast, { ToastVariant } from './Toast';

const DEFAULT_DURATION_MS = 8000;

export type ToastOptions = {
  message: ReactNode;
  variant?: ToastVariant;
  durationMs?: number;
  // Shows an "Undo this action" link. The toast is dismissed before this runs, so a
  // second click cannot trigger it again.
  onUndo?: () => void;
};

type ActiveToast = Required<Omit<ToastOptions, 'onUndo'>> &
  Pick<ToastOptions, 'onUndo'> & { id: number };

type ToastContextValue = {
  showToast: (options: ToastOptions) => void;
  dismissToast: () => void;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// Mounted once at the app root so a toast outlives the component that triggered it,
// e.g. a table row that leaves its section after a status change. Only one toast is
// shown at a time; showing a new one replaces the current one.
export const ToastProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const nextToastIdRef = useRef(0);

  const showToast = useCallback((options: ToastOptions): void => {
    // A replaced toast's focused element is removed without firing blur, and focus
    // does not move into the new toast. Hover is kept, since the host stays under the cursor.
    setIsFocused(false);
    nextToastIdRef.current += 1;
    setToast({
      id: nextToastIdRef.current,
      message: options.message,
      variant: options.variant ?? 'success',
      durationMs: options.durationMs ?? DEFAULT_DURATION_MS,
      onUndo: options.onUndo,
    });
  }, []);

  // Clears the pause state as well, since removing the toast from under the cursor or
  // focus does not fire mouseleave or blur and would otherwise leave the next toast paused.
  const dismissToast = useCallback((): void => {
    setToast(null);
    setIsHovered(false);
    setIsFocused(false);
  }, []);

  // Auto-dismiss pauses while the toast is hovered or focused so inline links stay
  // usable, and the full duration restarts once neither applies.
  const isPaused = isHovered || isFocused;
  useEffect(() => {
    if (!toast || isPaused) return undefined;
    const timeoutId = window.setTimeout(dismissToast, toast.durationMs);
    return () => window.clearTimeout(timeoutId);
  }, [toast, isPaused, dismissToast]);

  const handleUndo = useCallback((): void => {
    const onUndo = toast?.onUndo;
    dismissToast();
    onUndo?.();
  }, [toast, dismissToast]);

  const contextValue = useMemo(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {toast && (
        // The handlers only pause the auto-dismiss timer; the toast's own buttons and
        // links remain the interactive elements.
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div
          className="app-toast-host"
          onBlur={() => setIsFocused(false)}
          onFocus={() => setIsFocused(true)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Toast
            key={toast.id}
            message={toast.message}
            onClose={dismissToast}
            onUndo={toast.onUndo ? handleUndo : undefined}
            variant={toast.variant}
          />
        </div>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
